import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AlertFeedback, AlertVerdictSubmission } from 'app/shared/models/alert-feedback.model';
import { WaitingAlert } from 'app/shared/models/waiting-alert.model';
import { DEOAiAssistantService } from 'app/shared/screens/deo-ai-assistant/services/deo-ai-assistant.service';
import { AgentsService } from 'app/modules/agent-library/services/agents.service';
import { AlertsService } from 'app/shared/services/alerts/alerts.service';
import { NotificationsService } from 'app/shared/services/notifications/notifications.service';
import { Subject, filter, takeUntil } from 'rxjs';

/**
 * The Alerts tray: the anomalies raised by agents that are waiting for review.
 *
 * The one alerts-aware piece of the tray. It opens off the rail's Notification row through
 * `NotificationsService.openTrayRequested$` — the intent PLAN-2365 left for it — fills from
 * `AlertsService`, and hands Ask DEO to the assistant's own service so the widget never learns
 * about alerts.
 *
 * A reviewed alert leaves the waiting list, and therefore the badge count, at once. Its card keeps
 * its place showing the recorded verdict until the tray next loads: the reviewer sees what they
 * just decided rather than a row disappearing under the cursor.
 */
@Component({
  selector: 'gains-alerts-tray',
  standalone: false,
  templateUrl: './gains-alerts-tray.component.html',
  styleUrl: './gains-alerts-tray.component.scss'
})
export class GainsAlertsTrayComponent implements OnInit, OnDestroy {
  /**
   * Whether the navigation rail is collapsed, so the tray can sit beside it either way.
   *
   * The rail is `position: fixed`, so an expanded rail would otherwise be underneath the tray
   * rather than beside it.
   */
  @Input() sidenavCollapsed = true;

  /** Emits whenever the tray opens or closes, so the shell can get the rail out of its way. */
  @Output() openChanged$ = new EventEmitter<boolean>();

  /** Shifts the tray clear of the expanded rail. */
  @HostBinding('class.alerts-tray--rail-expanded')
  get isRailExpanded(): boolean {
    return !this.sidenavCollapsed;
  }

  isOpen = false;

  /** Anomalies rendered right now: those still waiting, plus any reviewed this visit. */
  displayedAlerts: WaitingAlert[] = [];

  /** Anomalies still awaiting a verdict — the same number the rail badge shows. */
  waitingCount = 0;

  readonly heading = 'Anomalies to review';

  readonly emptyMessage = 'Nothing waiting for review.';

  readonly footerNote = "Highest impact first, capped per agent. Your verdict feeds back into the agent's context.";

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _router: Router,
    private readonly _alertsService: AlertsService,
    private readonly _agentsService: AgentsService,
    private readonly _notificationsService: NotificationsService,
    private readonly _deoAiAssistantService: DEOAiAssistantService
  ) {}

  ngOnInit(): void {
    this._alertsService.waitingAlerts$.pipe(takeUntil(this.destroy$)).subscribe(alerts => this.syncDisplayedAlerts(alerts));

    this._notificationsService.openTrayRequested$.pipe(takeUntil(this.destroy$)).subscribe(() => this.toggle());

    // A tray belongs to the screen it was opened over.
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.close());

    this._alertsService.loadWaitingAlerts();

    // Nothing else loads the catalogue for a reviewer who has never opened /agent-library, and
    // `canAskDeo` needs it to know whether an anomaly's agent is released.
    this._agentsService.loadAgents();
  }

  /**
   * Whether Ask DEO is offered for this anomaly.
   *
   * Only for an agent that is actually released: a "Coming soon" agent has no conversation to open,
   * so the button would promise something that cannot happen. An agent the catalogue does not know
   * counts as unavailable for the same reason. The verdict buttons stay either way — a reviewer must
   * be able to judge an anomaly whether or not they can talk to the agent that raised it.
   */
  canAskDeo(alert: WaitingAlert): boolean {
    return this._agentsService.getAgent(alert.owningAgentId)?.isAvailable === true;
  }

  get isEmpty(): boolean {
    return this.displayedAlerts.length === 0;
  }

  /** The verdict recorded against an alert, which is what turns its card into a recorded line. */
  feedbackFor(alert: WaitingAlert): AlertFeedback | undefined {
    return this._alertsService.feedbackFor(alert.id);
  }

  onVerdictGiven(alert: WaitingAlert, submission: AlertVerdictSubmission): void {
    this._alertsService.recordFeedback(alert.id, submission.verdict, submission.reason);
  }

  /** Hands the anomaly and its owning agent to the assistant, then gets out of the way. */
  onAskDeo(alert: WaitingAlert): void {
    this._deoAiAssistantService.askAboutAlert({
      agentId: alert.owningAgentId,
      agentName: alert.owningAgentName,
      alertId: alert.id,
      alertTitle: alert.title,
      prompt: `Why did ${alert.title} get flagged?`
    });

    this.close();
  }

  close(): void {
    this.setOpen(false);
  }

  protected trackById(index: number, alert: WaitingAlert): string {
    return alert.id;
  }

  private toggle(): void {
    this.setOpen(!this.isOpen);
  }

  private setOpen(isOpen: boolean): void {
    if (this.isOpen === isOpen) {
      return;
    }

    this.isOpen = isOpen;
    this.openChanged$.emit(isOpen);
  }

  /**
   * Keeps the cards a reviewer is looking at where they are.
   *
   * A verdict removes its alert from the waiting list, so the list alone would drop the card
   * mid-review; anything already on screen with a recorded verdict stays, in place, and newly
   * raised anomalies come in on top.
   */
  private syncDisplayedAlerts(waitingAlerts: WaitingAlert[]): void {
    this.waitingCount = waitingAlerts.length;

    const waitingIds = new Set(waitingAlerts.map(alert => alert.id));
    const kept = this.displayedAlerts.filter(alert => waitingIds.has(alert.id) || !!this._alertsService.feedbackFor(alert.id));
    const keptIds = new Set(kept.map(alert => alert.id));
    const arrived = waitingAlerts.filter(alert => !keptIds.has(alert.id));

    this.displayedAlerts = [...arrived, ...kept];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
